import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useTheme,
  useAuth,
  config,
  useUser,
  useMerchant,
  merchant$,
} from "@zkt/eb-utility";
import { Layout, Spin } from "@zkt/components";
import { getEbUserCheckUserPathPermission } from "@zkt/open-api/esm/oauth2";
import { getCGetCustomThemeGetBoardCustomThemeByHotelId as setglobalStyles } from "@zkt/open-api/cjs/board";
import Topbar from "./components/topbar";
import Navbar from "./components/navbar";
import MicroApp from "./components/micro-app";
import IframeMicroApp from "./components/iframe-micro-app";
import HelpSidebar from "./components/help-sidebar";
import "./app.scss";
import ZktLogo from "@/assets/logo.svg";
import PageApp from "./components/page-app";
import CustomStyle from "./components/custom-style";
import { TypeBoardCustomTheme } from "./components/custom-style/constants";

const systemPages = ["/403", "/404"];
// 接口错误默认值
const defaultBoardCustomThem = {
  isOpen: "0",
};

function isEbActived() {
  // 不匹配所有的微应用，则跳转到ebooking
  const { pathname, hash } = window.location;
  return [
    ...globalThis.apps.map((x) => `/${x}`),
    "/#/iframe",
    ...systemPages,
  ].every((p) => !pathname.startsWith(p) && !hash.startsWith("#/iframe"));
}
function formatLink(link: string) {
  if (link?.includes("CURRENT_HOTEL_ID")) {
    return link.replace(
      /CURRENT_HOTEL_ID/g,
      String(merchant$.value?.merchantId)
    );
  }
  return link || undefined;
}

export default function App(): JSX.Element {
  const [theme] = useTheme();
  const { merchant } = useMerchant();
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [styleLoading, setSetStyleLoading] = useState(false);
  const { auth } = useAuth();
  const { user } = useUser();
  const [logoUrl, setLogoUrl] = useState<string>(ZktLogo);
  const [logoWidth, setLogoWidth] = useState(79);
  const [logoHeight, setLogoHeight] = useState(21);
  const [navbarList, setNavbarList] = useState<Array<unknown>>();
  const [customThemeStyles, setCustomThemeStyles] =
    useState<TypeBoardCustomTheme>(null);
  const [loadIframeApp, setLoadIframeApp] = useState(false);

  const inIframe = window.top !== window;
  if (inIframe) {
    const body = document.querySelector("body");
    const antSpinContainer = document.querySelector(".ant-spin-container");
    body.style.minWidth = "auto";
    if (antSpinContainer) {
      antSpinContainer.style.minHeight = "auto";
    }
  }

  useEffect(() => {
    // 避免多次请求,单独抽出来
    if (merchant) {
      setglobalStyles({ hotelId: merchant.merchantId })
        .then((res) => {
          // 成功写入
          setCustomThemeStyles(res);
        })
        .catch(() => {
          // 失败写入默认值
          setCustomThemeStyles(defaultBoardCustomThem);
        })
        .finally(() => {
          // 处理菜单替换问题(主动设置)
          setSetStyleLoading(true);
        });
    }
  }, [merchant]);

  const getHotelUrl = useCallback(() => {
    if (!auth?.token) return undefined;
    const { hash, origin } = window.location;
    // console.log(window.btoa(encodeURIComponent(origin)), "===>编码数据");
    const parentOrigin = window.btoa(encodeURIComponent(origin));
    const fragments = hash.split("/");
    if (fragments.length < 4) return undefined;
    const pathname = window.atob(decodeURIComponent(fragments[2]));
    return `//${config.hotelHost}${pathname}${
      pathname.includes("?") ? "&" : "?"
    }auth=${auth.token}&origin=${parentOrigin}`;
  }, [auth?.token]);

  const getEBUrl = useCallback(() => {
    if (!auth?.token) return undefined;
    const { href, origin } = window.location;
    const url = `//${config.ebookingHost}${href.replace(origin, "")}${
      // const url = `//local.zhiketong.net:8080${href.replace(origin, "")}${
      href.includes("?") ? "&" : "?"
    }`
      .replace(/auth=[^&]*/, "")
      .replace(/&+/g, "&");
    return `${url}auth=${auth.token}`;
  }, [auth?.token]);

  const CachedNavbar = useMemo(() => {
    return (
      user &&
      styleLoading && (
        <Navbar
          onCollapsed={setCollapsed}
          collapsed={collapsed}
          width={collapsed ? 50 : 180}
          logoUrl={logoUrl}
          logoWidth={logoWidth}
          logoHeight={logoHeight}
          setNavbarList={setNavbarList}
        />
      )
    );
  }, [user, styleLoading, collapsed, logoUrl, logoWidth, logoHeight]);
  const recursionNavbar = useCallback((tree) => {
    const result = [];
    const flattenData = (data) => {
      data.forEach((item) => {
        const flattenedItem = {
          link: item.link,
          href: item.href,
          name: item.name,
        };
        result.push(flattenedItem);
        if (item.children && item.children.length > 0) {
          flattenData(item.children);
        }
      });
    };

    flattenData(tree);
    const lists = result.find((item) => item.href);
    // console.log("222", lists);
    if (lists.href) {
      window.history.pushState(null, null, formatLink(lists.href));
    }
  }, []);

  const handleSetLoading = useCallback(
    (status) => {
      const { href } = window.location;
      // console.log(inIframe, href, "=====>打印数据");
      const arr = ["/card/card-iframe", "/member/point-iframe"]
      let flag = false;
      arr.forEach(item => {
        if(href.indexOf(item) > 0){
          flag = true;
        }
      })
      if (flag && inIframe) {
        console.log("=======>执行到这一步");
        setLoading(false);
        return;
      }
      setLoading(status);
    },
    [inIframe]
  );

  useEffect(() => {
    // 首次进入系统时检查员工是否有访问当前页面权限
    const { pathname, hash } = window.location;
    let path = hash ? hash.replace("#", "") : pathname;
    const queryStrIndex = path.indexOf("?");
    if (queryStrIndex > 0) {
      path = path.substring(0, queryStrIndex);
    }
    if (systemPages.includes(path)) return;
    if (path.startsWith("/iframe")) {
      const [, , bae64Str] = path.split("/");
      if (bae64Str) {
        try {
          path = window.atob(decodeURIComponent(bae64Str));
        } catch (error) {
          path = null;
        }
      }
    }
    if (!path) return;
    let newPath = path;
    if (path === "/") newPath = "/dashboard"; // 以"/"的方式接口不兼容,所以在这主动改变
    if (!navbarList) return;
    // console.log("111", path, newPath);
    getEbUserCheckUserPathPermission({ path: newPath }).then((authorized) => {
      // 有权限为ture
      setLoadIframeApp(true); // 等待初次权限校验结果返回再渲染ebooking和hotel应用，防止提前进入这2个项目触发他们内部的校验和跳转403的逻辑
      if (!authorized) {
        // 'dashboard'和'/'都是眺到概览页
        if (newPath === "/dashboard") {
          // 菜单栏信息如果不存在
          if (navbarList) {
            try {
              recursionNavbar(navbarList);
            } catch (err) {
              window.history.pushState(null, null, "/403");
            }
          }
        } else {
          window.history.pushState(null, null, "/403");
        }
      }
    });
  }, [navbarList, recursionNavbar]);

  return (
    <>
      {/* 通过子组件方式实现定制样式，后续需要考虑使用antd 动态主题方案：https://ant.design/docs/react/customize-theme-variable-cn */}
      <CustomStyle
        customThemeStyles={customThemeStyles}
        callback={(logo, lw, lh) => {
          setLogoUrl(logo);
          setLogoWidth(lw);
          setLogoHeight(lh);
        }}
      />
      <Layout className={`min-vh-100 position-relative theme-${theme}`}>
        {!inIframe && CachedNavbar}
        <Layout
          className={`site-layout site-layout-${collapsed && "collapsed"} ${
            inIframe && "in-iframe"
          }`}
        >
          {!inIframe && <Topbar />}
          <Layout.Content className="site-layout-background">
            <Spin spinning={loading} wrapperClassName="main-container">
              <PageApp
                onLoading={handleSetLoading}
                component={import("./pages/404")}
                activeWhen="/404"
                name="404"
              />
              <PageApp
                onLoading={handleSetLoading}
                component={import("./pages/403")}
                activeWhen="/403"
                name="403"
              />
              {globalThis.apps.map((app) => (
                <MicroApp
                  key={app}
                  name={`@zkt/eb-${app}`}
                  onLoading={handleSetLoading}
                  activeWhen={[`/${app}`]}
                />
              ))}
              {loadIframeApp ? (
                <>
                  <IframeMicroApp
                    name="php-hotel"
                    activeWhen={["/#/iframe/:params/hotel"]}
                    onLoading={handleSetLoading}
                    getUrl={getHotelUrl}
                  />
                  <IframeMicroApp
                    spa
                    name="ebooking"
                    activeWhen={isEbActived}
                    onLoading={handleSetLoading}
                    getUrl={getEBUrl}
                  />
                </>
              ) : null}
            </Spin>
          </Layout.Content>
          {!inIframe && <HelpSidebar />}
        </Layout>
      </Layout>
    </>
  );
}
